import React, { useEffect, useState } from "react";
import { ArrowLeft, Loader2, CheckCircle2, MoreVertical, Check, Home, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { updateCustomers } from "@/services/auth";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { verifyUPIID } from "@/services/customer";
import { getT } from '../i18n/translations';

const BankDetails = () => {
  const t = getT();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { data: customer } = useSelector((state) => state.customer);
  const bankList = customer?.customer?.bankInfo || [];
  const hasBankInfo = bankList.length > 0;

  const [loading, setLoading] = useState(false);
  const [ifscLoading, setIfscLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [bankName, setBankName] = useState("");
  const [ifscVerified, setIfscVerified] = useState(false);
  const [accountHolderName, setAccountHolderName] = useState("");

  const [formData, setFormData] = useState({
    ifscCode: "",
    accountNumber: "",
    confirmAccountNumber: "",
  });

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    if (!customer) dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    const handler = () => setMenuOpen(null);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const fetchBankName = async (ifsc) => {
    try {
      setIfscLoading(true);
      setIfscVerified(false);
      setBankName("");
      const res = await fetch(`https://ifsc.razorpay.com/${ifsc}`);
      const data = await res.json();
      if (data?.BANK) {
        setBankName(data.BANK);
        setIfscVerified(true);
      } else {
        toast.error(t.bank_errInvalidIfsc);
      }
    } catch {
      toast.error(t.bank_errIfscFetch);
    }
    setIfscLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedValue = name === "ifscCode" ? value.toUpperCase() : value;
    setFormData({ ...formData, [name]: updatedValue });
    if (name === "ifscCode") {
      setIfscVerified(false);
      setBankName("");
      if (updatedValue.length === 11) fetchBankName(updatedValue);
    }
  };

  const handleSubmit = async () => {
    if (!formData.ifscCode || !formData.accountNumber || !formData.confirmAccountNumber) {
      toast.error(t.bank_errFillAll);
      return;
    }
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      toast.error(t.bank_errMismatch);
      return;
    }
    if (!bankName) {
      toast.error(t.bank_errInvalidIfsc);
      return;
    }

    setLoading(true);
    const token = JSON.parse(localStorage.getItem("token_gen"));
    const customerCode = localStorage.getItem("customercode");

    let holderName = "";

    try {
      const verifyRes = await verifyUPIID(token?.access_token, {
        verification_type: "auto",
        accountNumber: formData.accountNumber,
        accountIfsc: formData.ifscCode,
        auto_priority: ["pennyless", "penny_drop", "paisa_drop"],
        consent: true,
      });

      if (verifyRes?.data?.data?.failure_reason === "INVALID ACCOUNT") {
        setLoading(false);
        toast.error(t.bank_errVerifyFailed);
        return;
      }
      holderName = verifyRes?.data?.data?.account_holder_name || "";
      setAccountHolderName(holderName);

    } catch {
      setLoading(false);
      toast.error(t.bank_errVerifyError);
      return;
    }

    const newBank = {
      ifscCode: formData.ifscCode,
      bankName,
      accountNumber: formData.accountNumber,
      accountHolderName: holderName,
      nickname: holderName || bankName,
      isDefault: bankList.length === 0,
    };

    const updatedBankList = [...bankList, newBank];

    await updateCustomers(token?.access_token, customerCode, {
      bankInfo: updatedBankList,
      upiIdVerified: true,
    });

    toast.success(t.bank_successAdded);
    await dispatch(fetchCustomerData(customerCode));
    setLoading(false);
    setShowForm(false);
    setFormData({ ifscCode: "", accountNumber: "", confirmAccountNumber: "" });
    setBankName("");
    setIfscVerified(false);
    setAccountHolderName("");
  };

  const handleDelete = async (indexToDelete) => {
    setLoading(true);
    const token = JSON.parse(localStorage.getItem("token_gen"));
    const customerCode = localStorage.getItem("customercode");

    const updatedList = bankList.filter((_, i) => i !== indexToDelete);
    const wasDefault = bankList[indexToDelete]?.isDefault;
    if (wasDefault && updatedList.length > 0) updatedList[0].isDefault = true;

    await updateCustomers(token?.access_token, customerCode, {
      bankInfo: updatedList,
      upiIdVerified: updatedList.length > 0,
    });

    toast.success(t.bank_successDeleted);
    await dispatch(fetchCustomerData(customerCode));
    setLoading(false);
    setMenuOpen(null);
  };

  const handleSetDefault = async (indexToDefault) => {
    setLoading(true);
    const token = JSON.parse(localStorage.getItem("token_gen"));
    const customerCode = localStorage.getItem("customercode");

    const updatedList = bankList.map((bank, i) => ({
      ...bank,
      isDefault: i === indexToDefault,
    }));

    await updateCustomers(token?.access_token, customerCode, { bankInfo: updatedList });
    toast.success(t.bank_successDefault);
    await dispatch(fetchCustomerData(customerCode));
    setLoading(false);
    setMenuOpen(null);
  };

  const defaultBank = bankList.find((b) => b.isDefault) || bankList[0];

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      {loading && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-10 w-10 text-[#CC0000]" />
        </div>
      )}

      <div className="w-full max-w-md min-h-screen flex flex-col bg-[#ffe5e5]">

        {/* HEADER */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => {
              if (showForm) {
                setShowForm(false);
                setFormData({
                  ifscCode: "",
                  accountNumber: "",
                  confirmAccountNumber: "",
                });
                setBankName("");
                setIfscVerified(false);
                setAccountHolderName("");
              } else {
                navigate(-1);
              }
            }}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-red-100"
          >
            <ArrowLeft className="w-5 h-5 text-[#111111]" />
          </button>

          <p className="text-[15px] font-semibold text-[#111111]">
            {showForm ? t.bank_addTitle : t.bank_pageTitle}
          </p>

          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-red-100"
          >
            <Home className="w-5 h-5 text-[#111111]" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="px-4 pb-8 space-y-3 flex-1">

          {showForm || !hasBankInfo ? (
            <>
              {/* ADD BANK FORM */}
              <div className="bg-white rounded-2xl px-5 py-5 shadow-md border border-red-100 mx-1 space-y-4">

                {/* IFSC */}
                <div>
                  <p className="text-[13px] font-medium text-[#111111] mb-1.5">
                    {t.bank_ifscLabel}
                  </p>

                  <div className="relative">
                    <input
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleChange}
                      placeholder={t.bank_ifscPlaceholder}
                      maxLength={11}
                      className="w-full border-[1.5px] border-red-200 rounded-xl px-4 py-3 text-[14px] text-[#111111] outline-none focus:border-[#CC0000] transition-colors placeholder:text-gray-400 pr-10"
                    />

                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {ifscLoading && (
                        <Loader2 className="animate-spin w-4 h-4 text-[#CC0000]" />
                      )}
                      {!ifscLoading && ifscVerified && (
                        <CheckCircle2 size={18} className="text-[#CC0000]" />
                      )}
                    </div>
                  </div>

                  {bankName && (
                    <p className="text-[12px] text-[#CC0000] mt-1 font-semibold flex items-center gap-1">
                      <CheckCircle2 size={12} />
                      {bankName}
                    </p>
                  )}
                </div>

                {/* ACCOUNT NUMBER */}
                <div>
                  <p className="text-[13px] font-medium text-[#111111] mb-1.5">
                    {t.bank_accountLabel}
                  </p>

                  <input
                    name="accountNumber"
                    value={formData.accountNumber}
                    onChange={handleChange}
                    placeholder={t.bank_accountPlaceholder}
                    className="w-full border-[1.5px] border-red-200 rounded-xl px-4 py-3 text-[14px] text-[#111111] outline-none focus:border-[#CC0000] transition-colors placeholder:text-gray-400"
                  />
                </div>

                {/* CONFIRM ACCOUNT */}
                <div>
                  <p className="text-[13px] font-medium text-[#111111] mb-1.5">
                    {t.bank_confirmLabel}
                  </p>

                  <input
                    name="confirmAccountNumber"
                    value={formData.confirmAccountNumber}
                    onChange={handleChange}
                    placeholder={t.bank_confirmPlaceholder}
                    className="w-full border-[1.5px] border-red-200 rounded-xl px-4 py-3 text-[14px] text-[#111111] outline-none focus:border-[#CC0000] transition-colors placeholder:text-gray-400"
                  />
                </div>
              </div>

              {/* SUBMIT BUTTON */}
              <button
                onClick={handleSubmit}
                disabled={
                  !ifscVerified ||
                  !formData.accountNumber ||
                  !formData.confirmAccountNumber
                }
                className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  width: "calc(100% - 8px)",
                  marginLeft: "4px",
                  backgroundColor: "#CC0000",
                }}
              >
                {t.bank_addBtn}
              </button>
            </>
          ) : (
            <>
              {/* BANK LIST */}
              {bankList.map((bank, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl px-5 py-4 shadow-md border border-red-100 mx-1 relative"
                >
                  <div className="flex justify-between items-start">
                    <div className="space-y-1 flex-1">

                      <p className="text-[13px] text-[#111111]">
                        <span className="font-semibold">Holder:</span>{" "}
                        {bank?.accountHolderName || "—"}
                      </p>

                      <p className="text-[13px] text-[#111111]">
                        <span className="font-semibold">Account:</span>{" "}
                        {bank?.accountNumber}
                      </p>

                      <p className="text-[13px] text-[#111111]">
                        <span className="font-semibold">IFSC:</span>{" "}
                        {bank?.ifscCode}
                      </p>

                      <p className="text-[13px] text-[#111111]">
                        <span className="font-semibold">Bank:</span>{" "}
                        {bank?.bankName}
                      </p>

                      {bank?.isDefault && (
                        <div className="flex items-center gap-1 border border-[#CC0000] rounded-lg px-2.5 py-1 w-fit mt-1">
                          <Check size={11} className="text-[#CC0000]" />
                          <span className="text-[11px] font-semibold text-[#CC0000]">
                            Default
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="relative">
                      <MoreVertical
                        size={18}
                        className="cursor-pointer text-gray-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuOpen(menuOpen === index ? null : index);
                        }}
                      />

                      {menuOpen === index && (
                        <div className="absolute right-0 top-6 bg-white shadow-lg rounded-xl p-1.5 w-36 z-50 border border-red-100">
                          {!bank?.isDefault && (
                            <p
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetDefault(index);
                              }}
                              className="px-3 py-2 text-[13px] hover:bg-red-50 cursor-pointer rounded-lg text-[#CC0000] font-medium"
                            >
                              Set Default
                            </p>
                          )}

                          <p
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(index);
                            }}
                            className="px-3 py-2 text-[13px] hover:bg-red-50 cursor-pointer rounded-lg text-red-600"
                          >
                            Delete
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* ACTION BUTTONS */}
              <button
                onClick={() =>
                  navigate("/upi-confirm", { state: { defaultBank } })
                }
                className="w-full py-4 rounded-xl text-[15px] font-bold text-white active:scale-[0.98] transition-transform"
                style={{
                  width: "calc(100% - 8px)",
                  marginLeft: "4px",
                  backgroundColor: "#CC0000",
                }}
              >
                Transfer Now
              </button>

              <button
                onClick={() => {
                  setShowForm(true);
                  setFormData({
                    ifscCode: "",
                    accountNumber: "",
                    confirmAccountNumber: "",
                  });
                  setBankName("");
                  setIfscVerified(false);
                  setAccountHolderName("");
                }}
                className="w-full py-4 rounded-xl text-[15px] font-semibold text-[#CC0000] border-[1.5px] border-[#CC0000] bg-white active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
                style={{
                  width: "calc(100% - 8px)",
                  marginLeft: "4px",
                }}
              >
                <Plus size={16} />
                Add Another Bank
              </button>
            </>
          )}
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t border-red-100 mt-auto">
          <p className="text-[13px] text-[#111111] uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
          </p>
        </div> */}
      </div>
    </div>
  );
};

export default BankDetails;